/**
 *  Created by piotr@revisual.io on 15/08/2025
 */
import { PLANS, QUOTAS, UsageFieldsMapping } from "../../../consts";

export const orderQuotas = (quotas, usage) =>
  Object.values(QUOTAS).sort((a, b) => {
    const quotaA = quotas[a];
    const quotaB = quotas[b];

    const usageKeyA = UsageFieldsMapping[a];
    const usageKeyB = UsageFieldsMapping[b];

    const usageA = usage[usageKeyA];
    const usageB = usage[usageKeyB];

    const isDefined = (val) => val !== undefined && val !== null;

    const getGroup = (quota, usage) => {
      if (quota > 0 && isDefined(usage)) return 0; // First group
      if (quota === -1) return 1; // Second group
      return 2; // Last group
    };

    const groupA = getGroup(quotaA, usageA);
    const groupB = getGroup(quotaB, usageB);

    return groupA - groupB;
  });

/**
 * The most recent paid plan this org has fallen off of (available=0,
 * price>0), regardless of why. `organizations_plans` rows carry no
 * plan_code/name (confirmed against the real check_session.json payload —
 * those fields only exist on the "active plan" projection, not the raw
 * history list), so a paid row is identified by `price > 0` instead.
 *
 * @param {Object} currentOrganization
 * @returns {Object|null}
 */
const getMostRecentLapsedPlan = (currentOrganization) => {
  const plans = currentOrganization?.model?.organizations_plans || [];
  return (
    plans
      .filter((p) => p.available * 1 === 0 && p.price > 0)
      .sort((a, b) => (b.expiry || b.created || 0) - (a.expiry || a.created || 0))[0] || null
  );
};

/**
 * "Reverted to free" (design_handoff_billing state 06) isn't a stored status
 * — subscription_deleted just drops the org onto a plain free plan row, the
 * same way a user-initiated cancellation eventually does once its paid
 * period lapses. The two are told apart by `is_terminated` on the most
 * recent non-available paid plan: 1 means the user cancelled on purpose
 * (expected, no band needed once it lapses to free); 0 means it lapsed
 * involuntarily (failed renewal) — that's the case this card should flag.
 *
 * `is_terminated` must gate the result AFTER picking the most recent lapsed
 * plan, not be part of the filter that picks it — a superseded trial row
 * (e.g. converted to paid) never gets is_terminated set at all, so folding
 * it into the filter lets that older, irrelevant row win over a more
 * recent, correctly-flagged voluntary cancellation.
 *
 * @param {Object} currentOrganization
 * @returns {Object|null} the lapsed paid plan row, or null if not reverted
 */
export const getRevertedPlan = (currentOrganization) => {
  const currentCode = currentOrganization?.subscription?.code;
  if (currentCode && currentCode !== PLANS.free) {
    return null;
  }

  const mostRecentLapsed = getMostRecentLapsedPlan(currentOrganization);

  if (!mostRecentLapsed || mostRecentLapsed.is_terminated * 1 === 1) {
    return null;
  }

  return mostRecentLapsed;
};

/**
 * True once this org is back on Free specifically because its most recent
 * paid plan was cancelled on purpose (is_terminated=1) — the mirror image
 * of getRevertedPlan. Used to suppress the "your free trial has already
 * been used" framing, which is misleading for someone who actually had a
 * real paid subscription and chose to cancel it, not someone whose trial
 * simply ran out.
 *
 * @param {Object} currentOrganization
 * @returns {boolean}
 */
export const wasCancelledVoluntarily = (currentOrganization) => {
  const currentCode = currentOrganization?.subscription?.code;
  if (currentCode && currentCode !== PLANS.free) {
    return false;
  }

  const mostRecentLapsed = getMostRecentLapsedPlan(currentOrganization);
  return Boolean(mostRecentLapsed && mostRecentLapsed.is_terminated * 1 === 1);
};

export const isNewPlanIsBelowUsage = (newPlan, currentOrganization) => {
  const quotas = newPlan.metadata;
  const usage = currentOrganization.model.usage;

  for (const key of Object.keys(UsageFieldsMapping)) {
    const usageKey = UsageFieldsMapping[key];
    const quota = Number(quotas[key]);
    const used = usage[usageKey] || 0;

    if (quota === -1) {
      continue;
    }
    if (quota > 0 && used > quota) {
      return true;
    }
  }

  return false;
};
